import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/common/Breadcrumb';
import CustomDropdown from '@/components/common/CustomDropdown';
import Card from '@/components/ui/cards/Card';
import CardHeader from '@/components/ui/cards/CardHeader';
import PerPageSelection from '@/components/ui/tables/PerPageSelection';
import { useSelector, useDispatch } from 'react-redux';
import validationErrors from '@/lib/validationErrors';
import { getOrderService, deleteOrderService } from '@/services/OrderServices';
import { cn } from '@/lib/utils';
import DataTableFilter from './component/DataTableFilter';
import DataTable from './component/DataTable';
import PritntPdfTable from '@/components/common/PritntPdfTable';

const PosOrdersPage = ({ pageTitle }) => {
    const dispatch = useDispatch();
    const { printTableRef, handleDownloadPdf } = PritntPdfTable('POS Orders Data');
    const [dataSource, setDataSource] = useState([]);
    const [totalRecord, setTotalRecord] = useState(0);
    const [activeDrawer, setActiveDrawer] = useState(null);
    const [editDataId, setEditDataId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const filterDivRef = useRef(null);
    const [dataFilter, setDataFilter] = useState({
        search: '',
        page: 1,
        limit: 10,
        extra_filter: { order_type: 1 },
    });

    // ← Insert Order Type as second column
    const tableColumn = [
        { name: 'Order ID',    isIncludePrint: true },
        { name: 'Order Type',  isIncludePrint: true },
        { name: 'Customer',    isIncludePrint: true },
        { name: 'Amount',      isIncludePrint: true },
        { name: 'Date',        isIncludePrint: true },
        { name: 'Status',      isIncludePrint: true },
        { name: 'Action',      isIncludePrint: false },
    ];

    useEffect(() => {
        getTableData();
    }, [dataFilter]);

    const handleDrawerOpen = (editId) => {
        setEditDataId(editId || '');
        setActiveDrawer(true);
        document.body.style.overflowY = 'hidden';
    };

    const onChangeFilter = (name, value) => {
        if (name === 'extra_filter') {
            const filterObj = { ...value, order_type: 1 };
            setDataFilter({ ...dataFilter, extra_filter: filterObj });
        } else {
            setDataFilter({ ...dataFilter, [name]: value });
        }
    };

    const handleFilterToggle = () => {
        if (!filterDivRef.current) return;
        const height = filterDivRef.current.scrollHeight;
        if (!isActive) {
            setIsActive(true);
            filterDivRef.current.style.height = `100%`;
        } else {
            setIsActive(false);
            filterDivRef.current.style.height = '0px';
        }
    };

    const getTableData = () => {
        setIsLoading(true);
        dispatch(getOrderService(dataFilter))
            .then((res) => {
                setTotalRecord(res?.data?.data?.total);
                setDataSource(res?.data?.data?.data);
            })
            .catch(({ message, errorData }) => {
                validationErrors(errorData, message);
            })
            .finally(() => setIsLoading(false));
    };

    const deleteRecord = (deleteId) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
        }).then((result) => {
            if (!result.isConfirmed) return;
            setIsLoading(true);
            dispatch(deleteOrderService(deleteId))
                .then(() => {
                    Swal.fire('Deleted!', 'Your record has been deleted.', 'success');
                    getTableData();
                })
                .catch(({ message, errorData }) => validationErrors(errorData, message))
                .finally(() => setIsLoading(false));
        });
    };

    return (
        <>
            <Breadcrumb pageTitle={pageTitle} />
            <Card>
                <CardHeader>
                    <h3 className="db-card-title">{pageTitle}</h3>
                    <div className="db-card-filter">
                        <div className="db-field-down-arrow">
                            <PerPageSelection
                                onChangeParPage={onChangeFilter}
                                limit={dataFilter.limit}
                            />
                        </div>
                        <button
                            className="db-card-filter-btn table-filter-btn"
                            onClick={handleFilterToggle}
                        >
                            <i className="lab lab-filter lab-font-size-14" />
                            <span>Filter</span>
                        </button>
                        <CustomDropdown
                            buttonText="Export"
                            buttonIcon="lab-file-export"
                            dropdownList={[
                                {
                                    icon: 'lab-printer-line',
                                    text: 'Print',
                                    dropDownAction: handleDownloadPdf,
                                },
                            ]}
                        />
                    </div>
                </CardHeader>

                <div
                    className={cn('table-filter-div', isActive ? 'active' : '')}
                    ref={filterDivRef}
                >
                    <DataTableFilter onChangeFilter={onChangeFilter} />
                </div>

                <div ref={printTableRef}>
                    <DataTable
                        isLoading={isLoading}
                        tableColumn={tableColumn}
                        dataSource={dataSource}
                        totalRecord={totalRecord}
                        dataFilter={dataFilter}
                        onChangePage={onChangeFilter}
                        deleteRecord={deleteRecord}
                    />
                </div>
            </Card>
        </>
    );
};

export default PosOrdersPage;
